// src/pages/LineageDashboard.tsx — Data Lineage & Pipeline Health Dashboard
//
// Layer-first data-quality dashboard for the data engineering team over the
// Fabric medallion framework. Governed: dashboards:data_lineage (Administration).
//
// Tabs: Overview (whole-of-framework picture per pipeline) | Bronze | Silver |
// Gold (each a deep layer view: KPIs, per-pipeline breakdown, daily trend, top
// error signatures with full messages, raw run log) | Datasets (lineage funnel).
// A GLOBAL source/pipeline filter narrows every tab to one pipeline — the core
// drill-down: pick a pipeline, find the issue, read the error.
//
// Data: /api/lineage/* (controlDB + dw_gold ctrl audit). Window 7/14/30/90 days.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Spinner, SpinnerSize, Dropdown, IDropdownOption, SearchBox,
  mergeStyleSets, keyframes, MessageBar, MessageBarType,
} from '@fluentui/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { CaretLeft, CaretDown, CaretRight, ArrowsClockwise, Stack, Info, ArrowSquareOut } from '@phosphor-icons/react';
import { accessService } from '../services/accessService';

// ── Palette ──────────────────────────────────────────────────────────────────
const ACCENT = '#475569';
const OK = '#16a34a'; const FAIL = '#dc2626'; const WARN = '#d97706';
const BRONZE = '#b45309'; const SILVER = '#64748b'; const GOLD = '#ca8a04';
const LAYER_COLOURS: Record<string, string> = { bronze: BRONZE, silver: SILVER, gold: GOLD };
const SOURCES = ['pulse', 'conquest', 'oracle_cx', 'PnR', 'payglobal', 'oracle_fusion', 'ms_teams', 'health_manager'];

type TabKey = 'overview' | 'bronze' | 'silver' | 'gold' | 'datasets';
const TABS: { key: TabKey; label: string; dot?: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'bronze',   label: 'Bronze',  dot: BRONZE },
  { key: 'silver',   label: 'Silver',  dot: SILVER },
  { key: 'gold',     label: 'Gold',    dot: GOLD },
  { key: 'datasets', label: 'Datasets & Lineage' },
];
const WINDOW_OPTIONS: IDropdownOption[] = [
  { key: 7, text: 'Last 7 days' }, { key: 14, text: 'Last 14 days' },
  { key: 30, text: 'Last 30 days' }, { key: 90, text: 'Last 90 days' },
];

// ── Payload types ─────────────────────────────────────────────────────────────
interface MatrixRow { source: string; bronzePct: number | null; silverPct: number | null; goldPct: number | null;
  lastRunUtc: string | null; runs: { total: number; passed: number; failPct: number | null } | null }
interface OverviewData {
  kpis: { bronzeFiles: number; bronzePct: number | null; connectivityDrops: number; bronzeFailed: number;
    silverMerges: number; silverPct: number | null; silverFailed: number; skipPct: number | null;
    goldJobs: number; goldPct: number | null; goldFailed: number;
    rowsRead: number; rowsInserted: number; rowsUpdated: number };
  matrix: MatrixRow[]; daily: { date: string; files: number; failed: number }[];
  meta: { days: number; generatedAt: string };
}
interface FailuresData {
  kpis: { openFailures7d: number; connectivityDrops: number; recurring: number; rejects: number };
  taxonomy: { cause: string; count: number }[];
  watchlist: { rejects: number; deduped: number; skipPct: number | null; dedupInstrumented: boolean };
  meta: { days: number };
}
interface PerSourceRow { source: string; volume: number; datasets: number; okPct: number | null; failed: number;
  connectivity?: number; rejects?: number; skipped?: number; inserted?: number; updated?: number;
  rowsIn?: number; staging?: number; avgSec?: number | null; maxSec?: number; lastRun: string | null }
interface TopError { source: string; table: string; cause: string; count: number; lastSeen: string; sample: string; fabricUrl?: string | null }
interface TopDuration { source: string; table: string; when: string; durSec: number; rows: number; status: string | null }
interface LayerData {
  layer: string; kpis: Record<string, number | null>;
  perSource: PerSourceRow[]; daily: { date: string; total: number; bad: number }[];
  topErrors: TopError[]; topDurations?: TopDuration[];
  fabricLinksEnabled?: boolean;
  meta: { days: number; source: string | null; generatedAt: string };
}
interface RunlogsData { layer: string; page: number; pageSize: number; total: number; rows: any[]; fabricLinksEnabled?: boolean }
interface DatasetRow { datasetKey: string; source: string; runs: number; lastBronze: string; lastBronzeStatus: string | null;
  lastSilver: string | null; lastSilverStatus: string | null; srcRows: number; bronzeRows: number; uniqueRows: number;
  inserted: number; updated: number }
interface TraceRun { runId: string; bronzeAt: string; silverAt: string | null; bronzeStatus: string | null;
  silverStatus: string | null; srcRows: number; bronzeRows: number; uniqueRows: number; rejects: number; dupPK: number;
  inserted: number; updated: number; filePath: string | null }

// ── Utils ─────────────────────────────────────────────────────────────────────
function fmtNum(n: number): string { return (n ?? 0).toLocaleString('en-AU'); }
function fmtBig(n: number): string {
  if (n >= 1e9) return (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'k';
  return String(n ?? 0);
}
function fmtWhen(v: string | null): string {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function fmtDur(sec: number | null | undefined): string {
  if (sec == null) return '—';
  if (sec >= 3600) return (sec / 3600).toFixed(1) + 'h';
  if (sec >= 60) return Math.round(sec / 60) + 'm';
  return sec + 's';
}
function healthColour(p: number | null): string {
  if (p == null) return '#9ca3af';
  if (p < 90) return FAIL;
  if (p < 98) return WARN;
  return OK;
}
// Staleness: most pipelines load daily — flag last-run older than 26h (amber) / 72h (red).
function staleStyle(v: string | null): React.CSSProperties {
  if (!v) return {};
  const ageH = (Date.now() - new Date(v).getTime()) / 36e5;
  if (ageH > 72) return { color: FAIL, fontWeight: 700 };
  if (ageH > 26) return { color: WARN, fontWeight: 700 };
  return {};
}
function downloadCsv(rows: any[], filename: string) {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]);
  const escCell = (v: any) => { const t = v == null ? '' : String(v); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
  const csv = [cols.join(','), ...rows.map(r => cols.map(c => escCell(r[c])).join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
const spinAnim = keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } });

// ── Styles ────────────────────────────────────────────────────────────────────
const s = mergeStyleSets({
  page: { padding: 32, minHeight: 'calc(100vh - 52px)', background: '#F5F5F5',
    fontFamily: "'Segoe UI', system-ui, sans-serif", '@media (max-width:768px)': { padding: 16 } } as any,
  container: { maxWidth: 1360, margin: '0 auto' },
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: 5, color: ACCENT, fontWeight: 600,
    fontSize: 13, cursor: 'pointer', background: 'none', border: 'none', padding: 0, marginBottom: 4 },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16,
    flexWrap: 'wrap' as const, gap: 8 },
  pageTitle: { fontSize: 26, fontWeight: 800, color: '#1A1A1A', letterSpacing: '-0.02em',
    display: 'flex', alignItems: 'center', gap: 10 },
  pageSub: { fontSize: 13, color: '#747474', marginTop: 2 },
  refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#fff',
    border: '1px solid #E1E1E1', borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#484848',
    cursor: 'pointer', selectors: { ':hover': { borderColor: ACCENT, color: ACCENT } } } as any,
  filterGroup: { display: 'flex', flexDirection: 'column' as const, gap: 3 },
  filterLabel: { fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' as const, letterSpacing: '0.07em' },
  tabBar: { display: 'flex', borderBottom: '2px solid #E1E1E1', marginTop: 16, marginBottom: 16, flexWrap: 'wrap' as const },
  tabBtn: { padding: '10px 18px', fontSize: 14, fontWeight: 600, background: 'none', border: 'none',
    cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -2,
    display: 'inline-flex', alignItems: 'center', gap: 7 },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 13, marginBottom: 16,
    '@media (max-width: 1100px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
    '@media (max-width: 560px)': { gridTemplateColumns: 'repeat(2, 1fr)' } } as any,
  kpi: { background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10, padding: '14px 16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  kl: { fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' as const,
    letterSpacing: '0.06em', marginBottom: 6 },
  kv: { fontSize: 25, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: '1', fontVariantNumeric: 'tabular-nums' as const },
  ks: { fontSize: 11, color: '#9CA3AF', marginTop: 5 },
  card: { background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10, padding: '16px 18px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16 },
  ct: { fontSize: 14, fontWeight: 700, margin: '0 0 1px' },
  cs: { fontSize: 12, color: '#9CA3AF', margin: '0 0 13px' },
  grid32: { display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16, '@media (max-width: 1000px)': { gridTemplateColumns: '1fr' } } as any,
  th: { textAlign: 'left' as const, padding: '7px 9px', fontWeight: 700, fontSize: 10.5, color: '#747474',
    background: '#F9FAFB', borderBottom: '1px solid #E1E1E1', whiteSpace: 'nowrap' as const,
    textTransform: 'uppercase' as const, letterSpacing: '0.03em' },
  td: { padding: '7px 9px', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' as const,
    fontVariantNumeric: 'tabular-nums' as const, fontSize: 12 },
  pill: { display: 'inline-block', padding: '2px 9px', borderRadius: 10, fontSize: 11, fontWeight: 700 },
  chip: { border: '1px solid #E1E1E1', borderRadius: 16, padding: '5px 14px', fontSize: 12.5, fontWeight: 600,
    color: '#484848', background: '#fff', cursor: 'pointer' },
  centeredMsg: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center',
    minHeight: 180, gap: 12, color: '#747474', fontSize: 14, textAlign: 'center' as const },
  accessBox: { background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10, padding: 56,
    textAlign: 'center' as const, color: '#747474' },
  hbar: { width: 74, height: 7, background: '#F3F4F6', borderRadius: 4, overflow: 'hidden', display: 'inline-block', verticalAlign: 'middle' },
  drawer: { border: '1px solid #E2E8F0', borderLeft: `3px solid ${FAIL}`, borderRadius: '0 8px 8px 0',
    background: '#F8FAFC', margin: '0 0 6px', padding: '12px 16px' },
  errPre: { background: '#0F172A', color: '#FCA5A5', borderRadius: 7, padding: '10px 14px', fontSize: 11,
    lineHeight: 1.55, overflowX: 'auto' as const, margin: 0, fontFamily: 'Consolas, monospace',
    whiteSpace: 'pre-wrap' as const, wordBreak: 'break-word' as const },
});
const tooltipStyle = { backgroundColor: '#fff', border: '1px solid #E1E1E1', borderRadius: 6, color: '#232323', fontSize: 12 };

function StatusPill({ v }: { v: string | null | undefined }) {
  const t = String(v ?? 'NULL');
  const map: Record<string, [string, string]> = {
    Succeeded: ['#F0FDF4', OK], ok: ['#F0FDF4', OK], Success: ['#F0FDF4', OK],
    failed: ['#FEF2F2', FAIL], Failed: ['#FEF2F2', FAIL],
    skipped: ['#F3F4F6', '#6B7280'], skipped_empty_source: ['#F3F4F6', '#6B7280'],
    NULL: ['#FFFBEB', WARN],
  };
  const [bg, fg] = map[t] || ['#F3F4F6', '#6B7280'];
  return <span className={s.pill} style={{ background: bg, color: fg }}>{t === 'NULL' ? 'NULL (connectivity)' : t}</span>;
}
// Deep-link to the Fabric monitoring UI for a pipeline run. Renders nothing when the
// backend couldn't build a URL (workspace not configured, or the run has no pipeline/run id).
function FabricLink({ url, label = 'Open in Fabric' }: { url?: string | null; label?: string }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600,
        color: ACCENT, textDecoration: 'none', whiteSpace: 'nowrap' }}>
      {label} <ArrowSquareOut size={12} weight="bold" />
    </a>
  );
}
function HealthCell({ p }: { p: number | null }) {
  const c = healthColour(p);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span className={s.hbar}><i style={{ display: 'block', height: '100%', width: `${p ?? 0}%`, background: c }} /></span>
      <span style={{ fontSize: 11, fontWeight: 700, color: c, width: 47, display: 'inline-block' }}>
        {p != null ? `${p.toFixed(1)}%` : '—'}
      </span>
    </span>
  );
}
function DailyChart({ data, colour }: { data: { date: string; total: number; bad: number }[]; colour: string }) {
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data.map(d => ({ ...d, ok: d.total - d.bad,
        label: new Date(d.date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) }))}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtBig} />
        <Tooltip contentStyle={tooltipStyle} />
        <Bar dataKey="ok" name="ok" stackId="a" fill={colour} opacity={0.35} />
        <Bar dataKey="bad" name="failed / connectivity" stackId="a" fill={FAIL} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function LineageDashboard() {
  const nav = useNavigate();
  // Deep-linkable state: ?tab=silver&source=oracle_fusion&days=30 — shareable drill links
  const urlInit = new URLSearchParams(window.location.search);
  const initTab = (['overview', 'bronze', 'silver', 'gold', 'datasets'] as TabKey[])
    .includes(urlInit.get('tab') as TabKey) ? (urlInit.get('tab') as TabKey) : 'overview';
  const initDays = [7, 14, 30, 90].includes(Number(urlInit.get('days'))) ? Number(urlInit.get('days')) : 7;
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>(initTab);
  const [days, setDays] = useState(initDays);
  const [source, setSource] = useState(urlInit.get('source') || '');   // GLOBAL pipeline filter ('' = all)
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [logStatus, setLogStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedErr, setExpandedErr] = useState<number | null>(null);
  const [expandedLog, setExpandedLog] = useState<number | null>(null);
  const [traceKey, setTraceKey] = useState<string | null>(null);

  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [dq, setDq] = useState<FailuresData | null>(null);
  const [layerData, setLayerData] = useState<LayerData | null>(null);
  const [runlogs, setRunlogs] = useState<RunlogsData | null>(null);
  const [datasets, setDatasets] = useState<DatasetRow[] | null>(null);
  const [trace, setTrace] = useState<TraceRun[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{ role?: string; allowedDashboards?: string[] }>('/me');
        if (!alive) return;
        const d = res.data;
        setHasAccess(d.role === 'admin'
          ? true
          : Array.isArray(d.allowedDashboards) && d.allowedDashboards.includes('data_lineage'));
      } catch { setHasAccess(false); }
    })();
    return () => { alive = false; };
  }, []);

  const isLayerTab = activeTab === 'bronze' || activeTab === 'silver' || activeTab === 'gold';

  const fetchTab = useCallback(async (tab: TabKey) => {
    if (!hasAccess) return;
    setLoading(true); setError(null);
    try {
      if (tab === 'overview') {
        const [o, f] = await Promise.all([
          accessService.get<OverviewData>('/lineage/overview', { params: { days } }),
          accessService.get<FailuresData>('/lineage/failures', { params: { days } }),
        ]);
        setOverview(o.data); setDq(f.data);
      } else if (tab === 'bronze' || tab === 'silver' || tab === 'gold') {
        const [l, rl] = await Promise.all([
          accessService.get<LayerData>('/lineage/layer', { params: { days, layer: tab, ...(source ? { source } : {}) } }),
          accessService.get<RunlogsData>('/lineage/runlogs', { params: { days, layer: tab, page,
            ...(source ? { source } : {}), ...(logStatus ? { status: logStatus } : {}), ...(search ? { search } : {}) } }),
        ]);
        setLayerData(l.data); setRunlogs(rl.data);
      } else {
        const r = await accessService.get<{ datasets: DatasetRow[] }>('/lineage/datasets',
          { params: { days: Math.max(days, 30), ...(search ? { search } : {}) } });
        setDatasets(r.data.datasets);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to load data');
    } finally { setLoading(false); }
  }, [hasAccess, days, source, page, logStatus, search]);

  useEffect(() => { if (hasAccess) fetchTab(activeTab); }, [activeTab, fetchTab, hasAccess]);
  useEffect(() => { setPage(1); setExpandedErr(null); setExpandedLog(null); }, [activeTab, source, logStatus, search, days]);

  // Keep the URL shareable (no history spam)
  useEffect(() => {
    const q = new URLSearchParams();
    if (activeTab !== 'overview') q.set('tab', activeTab);
    if (source) q.set('source', source);
    if (days !== 7) q.set('days', String(days));
    const qs = q.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [activeTab, source, days]);

  // Auto-refresh for ops/wall-monitor use
  useEffect(() => {
    if (!autoRefresh || !hasAccess) return;
    const id = window.setInterval(() => fetchTab(activeTab), 60000);
    return () => window.clearInterval(id);
  }, [autoRefresh, hasAccess, fetchTab, activeTab]);

  useEffect(() => {
    if (!hasAccess || !traceKey) return;
    setTrace(null);
    accessService.get<{ runs: TraceRun[] }>('/lineage/trace', { params: { datasetKey: traceKey } })
      .then(r => setTrace(r.data.runs ?? []))
      .catch(() => setTrace([]));
  }, [hasAccess, traceKey]);

  const refreshedAt = (() => {
    const d = overview?.meta.generatedAt || layerData?.meta.generatedAt;
    return d ? new Date(d).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : null;
  })();

  if (hasAccess === null) {
    return <div className={s.page}><div className={s.container} style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
      <Spinner size={SpinnerSize.large} label="Checking access…" /></div></div>;
  }
  if (hasAccess === false) {
    return <div className={s.page}><div className={s.container}>
      <button className={s.backBtn} onClick={() => nav('/dashboards')}><CaretLeft size={12} weight="bold" /> Dashboards</button>
      <div className={s.accessBox}>
        <div style={{ fontWeight: 700, fontSize: 18, color: '#1A1A1A', marginBottom: 8 }}>Access Required</div>
        <div style={{ fontSize: 14, maxWidth: 440, margin: '0 auto' }}>
          The Data Lineage &amp; Pipeline Health dashboard requires governed access.
          Submit an access request to your administrator to get started.
        </div>
      </div></div></div>;
  }

  // ── Layer tab renderer (shared by bronze/silver/gold) ──────────────────────
  const renderLayerTab = (layer: 'bronze' | 'silver' | 'gold') => {
    if (!layerData || layerData.layer !== layer) return null;
    const k = layerData.kpis as any;
    const colour = LAYER_COLOURS[layer];
    const kpis: { l: string; v: string; sub: string; c?: string }[] =
      layer === 'bronze' ? [
        { l: 'Files Copied', v: fmtBig(k.files), sub: `${fmtNum(k.datasets)} datasets`, c: colour },
        { l: 'Success', v: k.okPct != null ? `${k.okPct.toFixed(1)}%` : '—', sub: `${fmtNum(k.connectivity)} connectivity · ${fmtNum(k.failed)} failed`, c: healthColour(k.okPct) },
        { l: 'Rows Read', v: fmtBig(k.rowsRead), sub: `${fmtBig(k.rowsCopied)} copied` },
        { l: 'Data Read', v: fmtBig(Math.round((k.bytesRead || 0) / 1e6)) + ' MB', sub: `${k.avgMBs ?? '—'} MB/s avg` },
        { l: 'Avg Copy Time', v: fmtDur(k.avgSec), sub: `max ${fmtDur(k.maxSec)}` },
        { l: 'Connectivity Drops', v: fmtNum(k.connectivity), sub: 'NULL status — source unreachable', c: k.connectivity > 0 ? WARN : undefined },
      ] : layer === 'silver' ? [
        { l: 'Merge Operations', v: fmtBig(k.merges), sub: `${fmtNum(k.tables)} tables`, c: colour },
        { l: 'Success', v: k.okPct != null ? `${k.okPct.toFixed(2)}%` : '—', sub: `${fmtNum(k.failed)} failed`, c: healthColour(k.okPct) },
        { l: 'Rows Inserted', v: fmtBig(k.inserted), sub: `${fmtBig(k.updated)} updated` },
        { l: 'Rejected Rows', v: fmtNum(k.rejects), sub: 'quarantined (corrupt / NULL PK)', c: k.rejects > 0 ? FAIL : undefined },
        { l: 'PK Dedup', v: fmtNum(k.deduped), sub: k.deduped === 0 ? 'counter may not be instrumented' : 'duplicate-PK rows removed', c: k.deduped === 0 ? WARN : undefined },
        { l: 'Skip Rate', v: k.skipPct != null ? `${k.skipPct.toFixed(1)}%` : '—', sub: 'empty / no-new-data sources' },
      ] : [
        { l: 'SCD2 Jobs', v: fmtBig(k.jobs), sub: `${fmtNum(k.distinctJobs)} distinct jobs`, c: colour },
        { l: 'Success', v: k.okPct != null ? `${k.okPct.toFixed(1)}%` : '—', sub: `${fmtNum(k.failed)} failed jobs`, c: healthColour(k.okPct) },
        { l: 'Staging Rows', v: fmtBig(k.staging), sub: 'rows staged for merge' },
        { l: 'SCD2 Inserted', v: fmtBig(k.inserted), sub: `${fmtBig(k.closed)} closed (superseded)` },
        { l: 'Type-1 Updated', v: fmtBig(k.t1Updated), sub: 'in-place updates' },
        { l: 'Avg Job Time', v: fmtDur(k.avgSec), sub: `max ${fmtDur(k.maxSec)}` },
      ];

    return (<>
      <div className={s.kpiRow}>
        {kpis.map(x => (
          <div key={x.l} className={s.kpi}>
            <div className={s.kl}>{x.l}</div>
            <div className={s.kv} style={x.c ? { color: x.c } : {}}>{x.v}</div>
            <div className={s.ks}>{x.sub}</div>
          </div>
        ))}
      </div>

      <div className={s.grid32}>
        <div className={s.card}>
          <div className={s.ct}>Per-Pipeline Breakdown{source ? ` — ${source}` : ''}</div>
          <div className={s.cs}>Click a row to drill the whole dashboard into that pipeline</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th className={s.th}>Pipeline</th><th className={s.th}>{layer === 'gold' ? 'Jobs' : layer === 'silver' ? 'Merges' : 'Files'}</th>
                <th className={s.th}>{layer === 'gold' ? 'Distinct jobs' : layer === 'silver' ? 'Tables' : 'Datasets'}</th>
                <th className={s.th}>Success</th><th className={s.th}>Failed</th>
                {layer === 'bronze' && <th className={s.th}>Connectivity</th>}
                {layer === 'silver' && <><th className={s.th}>Ins / Upd</th><th className={s.th}>Rejects</th></>}
                {layer === 'gold' && <th className={s.th}>Staging rows</th>}
                <th className={s.th}>Last run</th>
              </tr></thead>
              <tbody>
                {layerData.perSource.map(r => (
                  <tr key={r.source} style={{ cursor: 'pointer', background: source === r.source ? '#F8FAFC' : undefined }}
                    onClick={() => setSource(source === r.source ? '' : r.source)}>
                    <td className={s.td} style={{ fontWeight: 600 }}>{r.source}</td>
                    <td className={s.td}>{fmtNum(r.volume)}</td>
                    <td className={s.td}>{fmtNum(r.datasets)}</td>
                    <td className={s.td}><HealthCell p={r.okPct} /></td>
                    <td className={s.td} style={r.failed > 0 ? { color: FAIL, fontWeight: 700 } : {}}>{fmtNum(r.failed)}</td>
                    {layer === 'bronze' && <td className={s.td} style={(r.connectivity || 0) > 0 ? { color: WARN, fontWeight: 700 } : {}}>{fmtNum(r.connectivity || 0)}</td>}
                    {layer === 'silver' && <>
                      <td className={s.td}>{fmtBig(r.inserted || 0)} / {fmtBig(r.updated || 0)}</td>
                      <td className={s.td} style={(r.rejects || 0) > 0 ? { color: FAIL, fontWeight: 700 } : {}}>{fmtNum(r.rejects || 0)}</td></>}
                    {layer === 'gold' && <td className={s.td}>{fmtBig(r.staging || 0)}</td>}
                    <td className={s.td} style={staleStyle(r.lastRun)} title="amber >26h · red >72h since last run">{fmtWhen(r.lastRun)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className={s.card}>
          <div className={s.ct}>Daily Volume &amp; Failures</div>
          <div className={s.cs}>{layer} operations per day · red = failed{layer === 'bronze' ? ' / connectivity' : ''}</div>
          <DailyChart data={layerData.daily} colour={colour} />
          {(layerData.topDurations?.length ?? 0) > 0 && (<>
            <div className={s.ct} style={{ marginTop: 14 }}>Slowest Operations</div>
            <div className={s.cs}>Duration outliers in window — investigate anything wildly above the average</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th className={s.th}>Pipeline</th><th className={s.th}>Table</th>
                <th className={s.th}>Duration</th><th className={s.th}>Rows</th><th className={s.th}>When</th></tr></thead>
              <tbody>
                {layerData.topDurations!.map((d, i) => (
                  <tr key={i}>
                    <td className={s.td}>{d.source}</td>
                    <td className={s.td} style={{ fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.table}</td>
                    <td className={s.td} style={d.durSec > 3600 ? { color: FAIL, fontWeight: 700 } : { fontWeight: 600 }}>{fmtDur(d.durSec)}</td>
                    <td className={s.td}>{fmtBig(d.rows)}</td>
                    <td className={s.td}>{fmtWhen(d.when)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>)}
        </div>
      </div>

      <div className={s.card}>
        <div className={s.ct}>Top Error Signatures — {layer}{source ? ` · ${source}` : ''}</div>
        <div className={s.cs}>Grouped by cause + table · click a row for the latest full error message</div>
        {layerData.topErrors.length === 0
          ? <div className={s.centeredMsg} style={{ minHeight: 90 }}>No {layer} failures in this window 🎉</div>
          : <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th className={s.th}></th><th className={s.th}>Pipeline</th><th className={s.th}>Table / dataset</th>
                  <th className={s.th}>Root cause</th><th className={s.th}>Count</th><th className={s.th}>Last seen</th></tr></thead>
                <tbody>
                  {layerData.topErrors.map((e, i) => (
                    <React.Fragment key={i}>
                      <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedErr(expandedErr === i ? null : i)}>
                        <td className={s.td}>{expandedErr === i ? <CaretDown size={12} /> : <CaretRight size={12} />}</td>
                        <td className={s.td}>{e.source}</td>
                        <td className={s.td} style={{ fontWeight: 600 }}>{e.table}</td>
                        <td className={s.td}>{e.cause}</td>
                        <td className={s.td}>{e.count >= 3
                          ? <span className={s.pill} style={{ background: '#FEF2F2', color: FAIL }}>×{e.count}</span>
                          : <span className={s.pill} style={{ background: '#F3F4F6', color: '#6B7280' }}>×{e.count}</span>}</td>
                        <td className={s.td}>{fmtWhen(e.lastSeen)}</td>
                      </tr>
                      {expandedErr === i && (
                        <tr><td className={s.td} colSpan={6} style={{ whiteSpace: 'normal' }}>
                          <div className={s.drawer}>
                            <pre className={s.errPre}>{e.sample || '(no error text recorded)'}</pre>
                            {e.fabricUrl && <div style={{ marginTop: 8 }}>
                              <FabricLink url={e.fabricUrl} label="Inspect a failing run in Fabric monitoring" />
                            </div>}
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>}
      </div>

      {/* Raw run log */}
      {runlogs && runlogs.layer === layer && (
        <div className={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div className={s.ct}>Raw Run Log — ctrl.{layer === 'gold' ? 'gold_meta_run_details' : layer + '_meta_run_details'}</div>
              <div className={s.cs}>Honours window / pipeline / status / search · click a row for the full record
                {(layer === 'bronze' || layer === 'silver') && runlogs.fabricLinksEnabled === false
                  ? ' · Fabric deep-links off (workspace id not set)' : ''}</div>
            </div>
            <div style={{ fontSize: 12, color: '#747474' }}>
              {fmtNum(runlogs.total)} rows · page {runlogs.page} / {Math.max(1, Math.ceil(runlogs.total / runlogs.pageSize))}
              <button className={s.chip} style={{ marginLeft: 10 }} disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</button>
              <button className={s.chip} style={{ marginLeft: 6 }} disabled={page >= Math.ceil(runlogs.total / runlogs.pageSize)} onClick={() => setPage(p => p + 1)}>Next ›</button>
              <button className={s.chip} style={{ marginLeft: 6 }}
                onClick={() => downloadCsv(runlogs.rows, `${layer}_run_log_page${runlogs.page}.csv`)}>⤓ CSV</button>
            </div>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th className={s.th}></th><th className={s.th}>Run</th><th className={s.th}>Pipeline</th><th className={s.th}>Table</th>
                {layer === 'bronze' && <><th className={s.th}>Rows read</th><th className={s.th}>Copied</th><th className={s.th}>Dur</th><th className={s.th}>MB/s</th></>}
                {layer === 'silver' && <><th className={s.th}>Ins</th><th className={s.th}>Upd</th><th className={s.th}>Del</th><th className={s.th}>Out</th></>}
                {layer === 'gold' && <><th className={s.th}>Staging</th><th className={s.th}>Ins</th><th className={s.th}>Upd</th><th className={s.th}>Dur</th></>}
                <th className={s.th}>Status</th>
              </tr></thead>
              <tbody>
                {runlogs.rows.map((r: any, i: number) => (
                  <React.Fragment key={r.id ?? i}>
                    <tr style={{ cursor: 'pointer', background: (r.status === 'failed' || r.status === 'Failed' || (layer !== 'gold' && r.status == null)) ? '#FFF7ED' : undefined }}
                      onClick={() => setExpandedLog(expandedLog === i ? null : i)}>
                      <td className={s.td}>{expandedLog === i ? <CaretDown size={12} /> : <CaretRight size={12} />}</td>
                      <td className={s.td}>{fmtWhen(r.t)}</td>
                      <td className={s.td}>{r.src}</td>
                      <td className={s.td} style={{ fontWeight: 600, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.tbl || r.job || '—'}</td>
                      {layer === 'bronze' && <>
                        <td className={s.td}>{fmtNum(Number(r.rowsRead) || 0)}</td><td className={s.td}>{fmtNum(Number(r.rowsCopied) || 0)}</td>
                        <td className={s.td} style={Number(r.durSec) > 3600 ? { color: FAIL, fontWeight: 700 } : {}}>{fmtDur(r.durSec != null ? Number(r.durSec) : null)}</td>
                        <td className={s.td}>{r.mbs != null ? Math.round(Number(r.mbs)) : '—'}</td></>}
                      {layer === 'silver' && <>
                        <td className={s.td}>{fmtNum(Number(r.ins) || 0)}</td><td className={s.td}>{fmtNum(Number(r.upd) || 0)}</td>
                        <td className={s.td} style={Number(r.del) > 0 ? { color: FAIL, fontWeight: 700 } : {}}>{fmtNum(Number(r.del) || 0)}</td>
                        <td className={s.td}>{fmtNum(Number(r.outRows) || 0)}</td></>}
                      {layer === 'gold' && <>
                        <td className={s.td}>{fmtNum(Number(r.staging) || 0)}</td><td className={s.td}>{fmtNum(Number(r.ins) || 0)}</td>
                        <td className={s.td}>{fmtNum(Number(r.upd) || 0)}</td><td className={s.td}>{fmtDur(r.durSec != null ? Number(r.durSec) : null)}</td></>}
                      <td className={s.td}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <StatusPill v={r.status} />
                          {r.fabricUrl && <FabricLink url={r.fabricUrl} label="Fabric" />}
                        </span>
                      </td>
                    </tr>
                    {expandedLog === i && (
                      <tr><td className={s.td} colSpan={9} style={{ whiteSpace: 'normal' }}>
                        <div className={s.drawer} style={{ borderLeftColor: (r.err || r.status === 'failed' || r.status === 'Failed') ? FAIL : ACCENT }}>
                          {r.fabricUrl && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10,
                              paddingBottom: 10, borderBottom: '1px solid #E5E7EB' }}>
                              <FabricLink url={r.fabricUrl} label="Open this run in Fabric monitoring" />
                              {r.pipelineRun && <span style={{ fontSize: 10.5, color: '#94A3B8' }}>run id&nbsp;
                                <code style={{ fontSize: 10.5, color: '#475569' }}>{String(r.pipelineRun)}</code></span>}
                            </div>
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px 18px', fontSize: 11.5, marginBottom: r.err ? 10 : 0 }}>
                            {Object.entries(r).filter(([kk]) => kk !== 'err' && kk !== 'fabricUrl').map(([kk, vv]) => (
                              <span key={kk}><b style={{ display: 'block', fontSize: 9.5, color: '#94A3B8', textTransform: 'uppercase' }}>{kk}</b>{vv == null ? '—' : String(vv)}</span>
                            ))}
                          </div>
                          {r.err && <pre className={s.errPre}>{r.err}</pre>}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>);
  };

  return (
    <div className={s.page}><div className={s.container}>
      <button className={s.backBtn} onClick={() => nav('/dashboards')}><CaretLeft size={12} weight="bold" /> Dashboards</button>

      <div className={s.headerRow}>
        <div>
          <div className={s.pageTitle}>
            <span style={{ width: 38, height: 38, borderRadius: 8, background: ACCENT, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Stack size={19} color="#fff" weight="bold" />
            </span>
            Data Lineage &amp; Pipeline Health
          </div>
          <div className={s.pageSub}>
            Fabric medallion framework · bronze → silver → gold · controlDB audit
            {refreshedAt ? ` · Updated ${refreshedAt}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={s.refreshBtn} onClick={() => setAutoRefresh(a => !a)}
            style={autoRefresh ? { borderColor: ACCENT, color: ACCENT, background: '#F1F5F9' } : {}}>
            {autoRefresh ? '● Auto 60s' : '○ Auto 60s'}
          </button>
          <button className={s.refreshBtn} disabled={loading} onClick={() => fetchTab(activeTab)}>
            <ArrowsClockwise size={14} weight="bold" style={loading ? { animation: `${spinAnim} 1s linear infinite` } : {}} />
            Refresh
          </button>
        </div>
      </div>

      {error && <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 12, borderRadius: 8 } }}>{error}</MessageBar>}

      {/* Global filters — Pipeline applies to every tab */}
      <div style={{ background: '#fff', border: '1px solid #E1E1E1', borderRadius: 8, padding: '13px 18px',
        display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
        <div className={s.filterGroup}>
          <span className={s.filterLabel}>Pipeline / source</span>
          <Dropdown options={[{ key: '', text: 'All pipelines' }, ...SOURCES.map(x => ({ key: x, text: x }))]}
            selectedKey={source} onChange={(_, o) => o && setSource(String(o.key))}
            styles={{ root: { minWidth: 160 }, dropdown: { fontSize: 13 } }} />
        </div>
        <div className={s.filterGroup}>
          <span className={s.filterLabel}>Run window</span>
          <Dropdown options={WINDOW_OPTIONS} selectedKey={days}
            onChange={(_, o) => o && setDays(Number(o.key))}
            styles={{ root: { minWidth: 140 }, dropdown: { fontSize: 13 } }} />
        </div>
        {isLayerTab && (
          <div className={s.filterGroup}>
            <span className={s.filterLabel}>Status (run log)</span>
            <Dropdown placeholder="All statuses"
              options={[
                { key: '', text: 'All statuses' },
                ...(activeTab === 'bronze' ? [{ key: 'Succeeded', text: 'Succeeded' }, { key: 'Failed', text: 'Failed' }, { key: 'NULL', text: 'NULL (connectivity)' }]
                  : activeTab === 'silver' ? [{ key: 'ok', text: 'ok' }, { key: 'failed', text: 'failed' }, { key: 'skipped', text: 'skipped' }, { key: 'skipped_empty_source', text: 'skipped_empty_source' }]
                  : [{ key: 'Success', text: 'Success' }, { key: 'Failed', text: 'Failed' }]),
              ]}
              selectedKey={logStatus} onChange={(_, o) => o && setLogStatus(String(o.key))}
              styles={{ root: { minWidth: 160 }, dropdown: { fontSize: 13 } }} />
          </div>
        )}
        {(isLayerTab || activeTab === 'datasets') && (
          <div className={s.filterGroup}>
            <span className={s.filterLabel}>Find table / dataset / run id</span>
            <SearchBox placeholder="e.g. incident, cmr_…" value={search}
              onChange={(_, v) => setSearch(v ?? '')} styles={{ root: { minWidth: 250 } }} />
          </div>
        )}
        {source && <span className={s.pill} style={{ background: '#F1F5F9', color: ACCENT, alignSelf: 'center' }}>
          drilled into: {source} <button style={{ background: 'none', border: 'none', color: ACCENT, cursor: 'pointer', fontWeight: 800 }} onClick={() => setSource('')}>×</button></span>}
      </div>

      <div className={s.tabBar}>
        {TABS.map(t => (
          <button key={t.key} className={s.tabBtn} onClick={() => setActiveTab(t.key)}
            style={{ color: activeTab === t.key ? ACCENT : '#747474',
              borderBottom: activeTab === t.key ? `2px solid ${ACCENT}` : '2px solid transparent' }}>
            {t.dot && <span style={{ width: 9, height: 9, borderRadius: 3, background: t.dot, display: 'inline-block' }} />}
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className={s.centeredMsg}><Spinner size={SpinnerSize.large} /><span>Loading {TABS.find(t => t.key === activeTab)?.label}…</span></div>}

      {/* ═══════ OVERVIEW ═══════ */}
      {activeTab === 'overview' && !loading && overview && (() => {
        const k = overview.kpis;
        const dqIssues = (dq?.kpis.rejects || 0) + (dq?.kpis.connectivityDrops || 0) + k.silverFailed + k.bronzeFailed + k.goldFailed;
        return (<>
          <div className={s.kpiRow}>
            <div className={s.kpi}><div className={s.kl}>Bronze Success</div>
              <div className={s.kv} style={{ color: healthColour(k.bronzePct) }}>{k.bronzePct != null ? `${k.bronzePct.toFixed(1)}%` : '—'}</div>
              <div className={s.ks}>{fmtBig(k.bronzeFiles)} files · {fmtBig(k.rowsRead)} rows</div></div>
            <div className={s.kpi}><div className={s.kl}>Silver Success</div>
              <div className={s.kv} style={{ color: healthColour(k.silverPct) }}>{k.silverPct != null ? `${k.silverPct.toFixed(2)}%` : '—'}</div>
              <div className={s.ks}>{fmtBig(k.silverMerges)} merges · {fmtBig(k.rowsInserted + k.rowsUpdated)} rows merged</div></div>
            <div className={s.kpi}><div className={s.kl}>Gold Success</div>
              <div className={s.kv} style={{ color: healthColour(k.goldPct) }}>{k.goldPct != null ? `${k.goldPct.toFixed(1)}%` : '—'}</div>
              <div className={s.ks}>{fmtBig(k.goldJobs)} SCD2 jobs</div></div>
            <div className={s.kpi} style={{ borderLeft: `3px solid ${dqIssues > 0 ? WARN : '#E1E1E1'}` }}><div className={s.kl}>DQ Issues (aggregate)</div>
              <div className={s.kv} style={{ color: dqIssues > 0 ? WARN : '#1A1A1A' }}>{fmtBig(dqIssues)}</div>
              <div className={s.ks}>failures + connectivity + rejects</div></div>
            <div className={s.kpi} style={{ borderLeft: `3px solid ${FAIL}` }}><div className={s.kl}>Failures</div>
              <div className={s.kv} style={{ color: FAIL }}>{fmtNum(k.bronzeFailed + k.silverFailed + k.goldFailed)}</div>
              <div className={s.ks}>{k.bronzeFailed}b · {k.silverFailed}s · {k.goldFailed}g</div></div>
            <div className={s.kpi}><div className={s.kl}>Rejected Rows</div>
              <div className={s.kv}>{fmtNum(dq?.kpis.rejects || 0)}</div>
              <div className={s.ks}>quarantined in silver</div></div>
          </div>

          <div className={s.grid32}>
            <div className={s.card}>
              <div className={s.ct}>Pipeline Health — bronze / silver / gold success per pipeline</div>
              <div className={s.cs}>Last {overview.meta.days} days · click a row to drill the whole dashboard into that pipeline</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th className={s.th}>Pipeline</th><th className={s.th}>Bronze</th><th className={s.th}>Silver</th>
                    <th className={s.th}>Gold</th><th className={s.th}>Runs (pass/total)</th><th className={s.th}>Last run</th></tr></thead>
                  <tbody>
                    {overview.matrix.map(m => (
                      <tr key={m.source} style={{ cursor: 'pointer', background: source === m.source ? '#F8FAFC' : undefined }}
                        onClick={() => { setSource(m.source); setActiveTab('bronze'); }}>
                        <td className={s.td} style={{ fontWeight: 600 }}>{m.source}</td>
                        <td className={s.td}><HealthCell p={m.bronzePct} /></td>
                        <td className={s.td}><HealthCell p={m.silverPct} /></td>
                        <td className={s.td}><HealthCell p={m.goldPct} /></td>
                        <td className={s.td}>{m.runs ? `${fmtNum(m.runs.passed)} / ${fmtNum(m.runs.total)}` : '—'}</td>
                        <td className={s.td} style={staleStyle(m.lastRunUtc)} title="amber >26h · red >72h since last run">{fmtWhen(m.lastRunUtc)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className={s.cs} style={{ marginTop: 10, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Info size={13} /> Run rollups depend on lineage keys — sparse before 2 Jul 2026 until the backfill lands.
              </div>
            </div>

            <div className={s.card}>
              <div className={s.ct}>Data Quality — issue mix</div>
              <div className={s.cs}>All layers · last {dq?.meta.days ?? overview.meta.days} days</div>
              {dq && dq.taxonomy.length > 0 ? (() => {
                const max = Math.max(1, ...dq.taxonomy.map(t => t.count));
                return dq.taxonomy.map(t => (
                  <div key={t.cause} style={{ display: 'grid', gridTemplateColumns: '190px 1fr 70px', alignItems: 'center',
                    gap: 10, fontSize: 12, padding: '5px 0', borderBottom: '1px solid #F3F4F6' }}>
                    <span style={{ fontWeight: 600 }}>{t.cause}</span>
                    <div style={{ height: 13, borderRadius: 4, background: t.cause.toLowerCase().includes('connectivity') ? WARN : FAIL,
                      width: `${Math.max(2, (t.count / max) * 100)}%`, opacity: .85 }} />
                    <span style={{ fontWeight: 800, textAlign: 'right' }}>{fmtNum(t.count)}</span>
                  </div>
                ));
              })() : <div className={s.centeredMsg} style={{ minHeight: 80 }}>No issues in window 🎉</div>}
              {dq && !dq.watchlist.dedupInstrumented && (
                <div className={s.cs} style={{ marginTop: 10, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Info size={13} /> rows_deduped is 0 everywhere — dedup telemetry may not be instrumented.
                </div>
              )}
            </div>
          </div>

          <div className={s.card}>
            <div className={s.ct}>Daily Load Activity — silver merges</div>
            <div className={s.cs}>Last 14 days · red = failed</div>
            <DailyChart data={overview.daily.map(d => ({ date: d.date, total: d.files, bad: d.failed }))} colour="#94A3B8" />
          </div>
        </>);
      })()}

      {/* ═══════ BRONZE / SILVER / GOLD ═══════ */}
      {activeTab === 'bronze' && !loading && renderLayerTab('bronze')}
      {activeTab === 'silver' && !loading && renderLayerTab('silver')}
      {activeTab === 'gold' && !loading && renderLayerTab('gold')}

      {/* ═══════ DATASETS & LINEAGE ═══════ */}
      {activeTab === 'datasets' && !loading && datasets && (<>
        {traceKey && (
          <div className={s.card}>
            <div className={s.ct}>Trace — {traceKey}</div>
            <div className={s.cs}>Row-count funnel (latest run with lineage keys) · <button style={{ background: 'none', border: 'none', color: ACCENT, cursor: 'pointer', fontWeight: 700, fontSize: 12 }} onClick={() => setTraceKey(null)}>close ×</button></div>
            {!trace && <Spinner size={SpinnerSize.medium} label="Tracing…" />}
            {trace && trace.length === 0 && <div className={s.centeredMsg} style={{ minHeight: 90 }}>No lineage rows for this dataset yet (keys pending backfill).</div>}
            {trace && trace.length > 0 && (() => {
              const t = trace[0];
              const rows = [
                { label: 'Source rows read', v: t.srcRows, c: '#0891B2' },
                { label: 'Bronze (after dedup)', v: t.bronzeRows, c: BRONZE },
                { label: 'Silver unique records', v: t.uniqueRows, c: SILVER },
                { label: 'Merged (ins + upd)', v: t.inserted + t.updated, c: '#334155' },
              ];
              const max = Math.max(1, ...rows.map(r => r.v));
              return (<>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {rows.map(r => (
                    <div key={r.label} style={{ display: 'grid', gridTemplateColumns: '190px 1fr 130px', alignItems: 'center', gap: 12, fontSize: 12 }}>
                      <span>{r.label}</span>
                      <div style={{ height: 24, borderRadius: 5, background: r.c, width: `${Math.max(4, (r.v / max) * 100)}%`,
                        display: 'flex', alignItems: 'center', paddingLeft: 9, color: '#fff', fontWeight: 700 }}>{fmtNum(r.v)}</div>
                      <span style={{ fontSize: 11, color: '#747474', textAlign: 'right' }}>
                        {r.label.startsWith('Silver') && t.rejects > 0 ? `−${fmtNum(t.rejects)} rejects` :
                         r.label.startsWith('Bronze') && t.srcRows > t.bronzeRows ? `−${fmtNum(t.srcRows - t.bronzeRows)} dups` : ''}
                      </span>
                    </div>
                  ))}
                </div>
                <div className={s.cs} style={{ marginTop: 10, marginBottom: 0 }}>
                  Latest run {fmtWhen(t.bronzeAt)} · bronze <StatusPill v={t.bronzeStatus} /> · silver <StatusPill v={t.silverStatus} />
                  {t.dupPK > 0 ? ` · ${fmtNum(t.dupPK)} duplicate-PK rows removed` : ''}
                </div>
              </>);
            })()}
          </div>
        )}
        <div className={s.card}>
          <div className={s.ct}>Dataset Registry</div>
          <div className={s.cs}>{fmtNum(datasets.length)} datasets · latest run per dataset · click to trace</div>
          <div style={{ overflowX: 'auto', maxHeight: 560, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th className={s.th}>Pipeline</th><th className={s.th}>Dataset</th><th className={s.th}>Runs</th>
                <th className={s.th}>Last rows in</th><th className={s.th}>Ins / Upd</th><th className={s.th}>Bronze</th>
                <th className={s.th}>Silver</th><th className={s.th}>Last load</th></tr></thead>
              <tbody>
                {datasets.filter(d => !source || d.source === source).map(d => (
                  <tr key={d.datasetKey} style={{ cursor: 'pointer', background: traceKey === d.datasetKey ? '#F8FAFC' : undefined }}
                    onClick={() => setTraceKey(d.datasetKey)}>
                    <td className={s.td}>{d.source}</td>
                    <td className={s.td} style={{ fontWeight: 600 }}>{d.datasetKey}</td>
                    <td className={s.td}>{fmtNum(d.runs)}</td>
                    <td className={s.td}>{fmtNum(d.srcRows)}</td>
                    <td className={s.td}>{fmtNum(d.inserted)} / {fmtNum(d.updated)}</td>
                    <td className={s.td}><StatusPill v={d.lastBronzeStatus} /></td>
                    <td className={s.td}><StatusPill v={d.lastSilverStatus} /></td>
                    <td className={s.td}>{fmtWhen(d.lastBronze)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>)}

    </div></div>
  );
}
